/**
 * created by hezq on 2019-10-11
 *
 * description: 基于百度webuploader上传控件的封装，支持多文件、分片、断点续传
 * 			    更多api参考：http://fex.baidu.com/webuploader/
 *
 */

import WebUploader from 'webuploader';

export default {
	props: {
		// 上传目录
		dir: {
			type: String,
			default: '~'
		},
		// 上传地址
		url: {
			type: String,
			required: true
		},
		// 选择文件的按钮ID
		btnID: {
			type: String,
			required: true
		},
		// 是否开起同时选择多个文件能力，默认不开启
		multiple: {
			type: Boolean,
			default: false
		},
		// 允许的文件后缀，不带点，多个用逗号分割
		extensions: {
			type: String,
			default: ''
		},
		// 如 image/* 多个用逗号分割
		mimeTypes: {
			type: String,
			default: ''
		},
		// 是否开启自动上传，默认开启
		auto: {
			type: Boolean,
			default: true
		},
		// 文件上传请求的参数表，每次发送都会发送此对象中的参数，必须传token ssid等重要参数
		formData: {
			type: Object,
			required: true
		},
		// 限制所有文件的大小 500 M  
		fileSizeLimit: {
			type: Number,
			default: 500
		},
		// 验证文件总数量, 超出则不允许加入队列，默认10个文件
		fileNumLimit: {
			type: Number,
			default: 10
		},
		// 验证单个文件大小是否超出限制, 超出则不允许加入队列，默认最大50M
		fileSingleSizeLimit: {
			type: Number,
			default: 50
		},
		// 去重， 根据文件名字、文件大小和最后修改时间来生成hash Key，默认开启
		duplicate: {
			type: Boolean,
			default: false
		},
		// 上传并发数。允许同时最大上传进程数 [默认值：1]
		threads: {
			type: Number,
			default: 1
		},
		// 图片像素-宽度精确设定
		pixelsWidth: {
			type: Number
		},
		// 图片像素-高度精确设定
		pixelsHeight: {
			type: Number
		},
		// 图片像素-宽度限制设定
		pixelsWidthLimit: {
			type: Number
		},
		// 图片像素-高度限制设定
		pixelsHeightLimit: {
			type: Number
		},
	},

	computed: {
		getExtensions() {
			if (this.type === 'picture') {
				return 'gif,jpg,jpeg,bmp,png';
			} else {
				return this.extensions;
			}
		},
		getMimeTypes() {
			if (this.type === 'picture') {
				return 'image/*';
			} else {
				return this.mimeTypes;
			}
		},
	},
	data() {
		return {
			// 文件数量
			count: 0,
			// 上传实例
			uploader: null,
			// 队列上传进度 备用
			percentage: 0,
			// 要分片默认大小为 5M,
			maxChunkSize: 5 * 1024 * 1024,
			// 错误码
			errorMsg: {
				'F_DUPLICATE': '文件重复限制',
				'F_EXCEED_SIZE': '单个文件大小限制',
				'P_PIXELS_LIMIT': '图片像素大小限制',
				'Q_TYPE_DENIED': '文件类型限制',
				'Q_EXCEED_SIZE_LIMIT': '所有文件大小总和限制',
				'Q_EXCEED_NUM_LIMIT': '文件个数限制'
			}
		};
	},

	methods: {
		/**
		 * description: 注册上传控件
		 *
		 * created by hezq on 2019-10-11
		 *
		 */
		registerUploader() {
			let uploaderVM = this;

			WebUploader.Uploader.register({
				// 用来向队列中添加文件
				'add-file': 'addFile',
				// 整个文件上传前 
				'before-send-file': 'beforeSendFile',
				// 每个分片上传前 
				'before-send': 'beforeSend',
				// 分片上传完毕
				'after-send-file': 'afterSendFile',
			}, {
				addFile: function(files) {
					// 遍历files中的文件, 可以过滤掉不满足规则的，目前不添加此功能
				},
				beforeSendFile: function(file) {
					// 在文件发送之前request，此时还没有分片（如果配置了分片的话），可以用来做文件整体md5验证
					let deferred = WebUploader.Deferred();
					// 计算文件的唯一标记 fileMd5，用于断点续传  
					// 如果 md5File(file) 方法里只写一个 file 参数则计算 MD5 值会很慢 所以加了后面的参数：5 * 1024 * 1024  
					(new WebUploader.Uploader()).md5File(file, 0, uploaderVM.maxChunkSize).progress(function(percentage) {})
						.then(function(val) {
							// 记录下文件的唯一标识
							file.fileMd5 = val;
							// 获取文件信息后进入下一步    
							deferred.resolve();
						});
					return deferred.promise();
				},
				beforeSend: function(block) {
					// 在分片发送之前request，可以用来做分片验证，如果此分片已经上传成功了，可返回一个rejected promise来跳过此分片上传
				},
				afterSendFile: function(file) {
					// 分片上传完之后，可以通知后端合并
					// 目前不添加此功能
				}
			});
		},

		/**
		 * description: 创建上传实例
		 *
		 * created by hezq on 2019-10-11
		 *
		 */
		createUploader() {
			let uploaderVM = this;

			this.uploader = WebUploader.create({
				// 选择文件后是否自动上传  
				auto: false,
				// 开启分片上传  
				chunked: true,
				// 分片大小
				chunkSize: uploaderVM.maxChunkSize,
				// 如果某个分片由于网络问题出错，允许自动重传多少次
				chunkRetry: 3,
				// 上传并发数。允许同时最大上传进程数 [默认值：3]
				threads: uploaderVM.threads,
				// 是否重复上传（同时选择多个一样的文件），true 可以重复上传  
				duplicate: uploaderVM.duplicate,
				// 文件上传请求的参数表，每次发送都会发送此对象中的参数
				formData: uploaderVM.formData,
				// 上传当前分片时预处理下一分片  
				prepareNextFile: true,
				// swf 文件路径    
				swf: './Uploader.swf',
				// 文件接收服务端地址  
				server: uploaderVM.url,
				// 验证文件总大小是否超出限制, 超出则不允许加入队列
				fileSizeLimit: uploaderVM.fileSizeLimit * 1024 * 1024,
				// 验证文件总数量, 超出则不允许加入队列
				fileNumLimit: uploaderVM.fileNumLimit,
				// 验证单个文件大小是否超出限制, 超出则不允许加入队列
				fileSingleSizeLimit: uploaderVM.fileSingleSizeLimit * 1024 * 1024,
				// 选择文件的按钮容器相关信息
				pick: {
					// 按钮外层div的ID
					id: '#' + uploaderVM.btnID,
					// 按钮文字
					innerHTML: uploaderVM.btnName,
					// 是否可以批量上传，true 可以同时选择多个文件
					multiple: uploaderVM.multiple
				},
				// 不压缩 image, 默认如果是 jpeg，文件上传前会先压缩再上传！  
				resize: false,
				accept: {
					// 允许上传的文件后缀，不带点，多个用逗号分割  
					extensions: uploaderVM.getExtensions,
					// 文件类型 
					mimeTypes: uploaderVM.getMimeTypes
				}
			});
		},

		/**
		 * description: 触发上传过程中的事件
		 *
		 * created by hezq on 2019-10-11
		 *
		 */
		doUploaderEvents() {
			let uploaderVM = this;

			// 当文件被加入队列之前触发，此事件的handler返回值为false，则此文件不会被添加进入队列
			this.uploader.on('beforeFileQueued', function(file) {

				// 上传文件数量限制校验
				if (uploaderVM.count >= uploaderVM.fileNumLimit) {
					uploaderVM.$message({
						showClose: true,
						message: `${uploaderVM.errorMsg['Q_EXCEED_NUM_LIMIT']}【错误码：Q_EXCEED_NUM_LIMIT】`,
						type: 'error',
						duration: 3000
					});
					// 清空队列
					uploaderVM.reset();
					return false;
				}

				// 事件通知【before-file-queued】【File对象】
				uploaderVM.$emit('before-file-queued', file);
			});

			// 当有文件被添加进队列的时候（点击上传文件按钮，弹出文件选择框，选择完文件点击确定后触发的事件）
			this.uploader.on('fileQueued', function(file) {
				if (uploaderVM.pixelsWidth && uploaderVM.pixelsHeight || uploaderVM.pixelsWidthLimit || uploaderVM.pixelsHeightLimit) {
					uploaderVM.uploader.makeThumb(file, function(error, ret) {
						let imgWidth = file._info.width;
						let imgHeight = file._info.height;

						if (uploaderVM.pixelsWidth && uploaderVM.pixelsHeight && !(imgWidth == uploaderVM.pixelsWidth && imgHeight ==
								uploaderVM.pixelsHeight)) {
							uploaderVM.$message({
								showClose: true,
								message: `图片像素大小必须为${uploaderVM.pixelsWidth}*${uploaderVM.pixelsHeight}【错误码：P_PIXELS_LIMIT】`,
								type: 'error',
								duration: 3000
							});
							// 清空队列
							uploaderVM.reset();
							return false;
						} else if (uploaderVM.pixelsWidthLimit && imgWidth > uploaderVM.pixelsWidthLimit || uploaderVM.pixelsHeightLimit && imgWidth > uploaderVM.pixelsHeightLimit) {
							uploaderVM.$message({
								showClose: true,
								message: `${uploaderVM.errorMsg['P_PIXELS_LIMIT']}【错误码：P_PIXELS_LIMIT】`,
								type: 'error',
								duration: 3000
							});
							// 清空队列
							uploaderVM.reset();
							return false;
						} else {
							// 文件数量+1
							uploaderVM.count++;
							
							// 如果需要自动上传
							if (uploaderVM.auto || uploaderVM.type === 'picture') {
								// 调用上传方法
								uploaderVM.upload();
							}
							
							// 事件通知【file-queue】【File对象】【File数组】
							uploaderVM.$emit('file-queue', file, uploaderVM.getFiles());
							
							// 记录上传文件列表
							if (!uploaderVM.type) {
								uploaderVM.fileList = uploaderVM.getFiles();
							}
						}
					});
				} else {
					// 文件数量+1
					uploaderVM.count++;
					
					// 如果需要自动上传
					if (uploaderVM.auto || uploaderVM.type === 'picture') {
						// 调用上传方法
						uploaderVM.upload();
					}
					
					// 事件通知【file-queue】【File对象】【File数组】
					uploaderVM.$emit('file-queue', file, uploaderVM.getFiles());

					// 记录上传文件列表
					if (!uploaderVM.type) {
						uploaderVM.fileList = uploaderVM.getFiles();
					}
				}

			});

			// 当一批文件被加入到队列触发
			// this.uploader.on('filesQueued', function(files) {
			// 	// 如果需要自动上传
			// 	if (uploaderVM.auto || uploaderVM.type === 'picture') {
			// 		// 调用上传方法
			// 		uploaderVM.upload();
			// 	}

			// 	// 事件通知【files-queue】【File数组】
			// 	uploaderVM.$emit('files-queue', files, uploaderVM.getFiles());

			// 	// 记录上传文件列表
			// 	if (!uploaderVM.type) {
			// 		uploaderVM.fileList = uploaderVM.getFiles();
			// 	}

			// });

			// 当文件被移除队列后触发
			this.uploader.on('fileDequeued', function(file) {
				// 文件数量-1
				uploaderVM.count--;
				// 事件通知【file-dequeued】【File对象】【Files数组】
				uploaderVM.$emit('file-dequeued', file, uploaderVM.getFiles());
				// 记录上传文件列表
				if (!uploaderVM.type) {
					uploaderVM.fileList = uploaderVM.getFiles();
				}
			});

			// 当 uploader 被重置的时候触发
			this.uploader.on('reset', function() {
				uploaderVM.count = 0;
				// 初始化上传进度
				uploaderVM.percentage = 0;
				// 事件通知【reset】
				uploaderVM.$emit('reset');
			});

			// 当开始上传流程时触发
			this.uploader.on('startUpload', function() {
				// 按钮模式
				if (!uploaderVM.type) {
					// 修改按钮文字
					uploaderVM.btnName = '开始上传';
				}
				// 加载中
				if (uploaderVM.type !== 'custom') {
					uploaderVM.loading = true;
				}

				// 事件通知【start-upload】
				uploaderVM.$emit('start-upload');
			});

			// 当开始上传流程暂停时触发
			this.uploader.on('stopUpload', function() {
				// 事件通知【stop-upload】
				uploaderVM.$emit('stop-upload');
			});

			// 某个文件开始上传前触发，一个文件只会触发一次
			this.uploader.on('uploadStart', function(file) {
				// 事件通知【upload-start】【File对象】
				uploaderVM.$emit('upload-start', file);
			});

			//当某个文件的分块在发送前触发
			this.uploader.on('uploadBeforeSend', function(object, data, headers) {
				// 往headers-Authorization里设置token
				headers.Authorization = uploaderVM.formData.token || '';

				// 如果传入了IP，设置headers部分属性【需求：物流IP绑定】
				if (uploaderVM.formData.ip) {
					headers['x-forwarded-for'] = uploaderVM.formData.ip;
					headers['WL-Proxy-Client-IP'] = uploaderVM.formData.ip;
					headers['HTTP_CLIENT_IP'] = uploaderVM.formData.ip;
					headers['HTTP_X_FORWARDED_FOR'] = uploaderVM.formData.ip;
				}

				// 后端需要的参数
				data.fileMd5 = object.file.fileMd5;
				data.dirId = uploaderVM.dirId;
				data.fileLength = data.size;
				data.groupName = object.file.groupName;
				data.filePath = object.file.filePath;
				data.chunks = object.chunks;
				data.chunk = object.chunk;
				data.chunkSize = object.end - object.start;
				data.maxChunkSize = uploaderVM.maxChunkSize;
				data.ssid = uploaderVM.formData.ssid || '';

				// 删除token删除
				delete data.token;
			});

			//当某个文件上传到服务端响应后，会派送此事件来询问服务端响应是否有效。
			this.uploader.on('uploadAccept', function(object, res) {
				if (res.code === 200) {
					if (res.data) {
						object.file.filePath = res.data.fdfsInfo ? res.data.fdfsInfo.filePath || '' : '';
						object.file.groupName = res.data.fdfsInfo ? res.data.fdfsInfo.groupName || '' : '';
					}
				} else {
					// 删除文件的上传进度
					delete object.file.percentage;

					// 按钮模式
					if (!uploaderVM.type) {
						// 修改按钮文字
						uploaderVM.btnName = uploaderVM.$options.data().btnName;
					}

					// 取消加载中
					if (uploaderVM.type !== 'custom') {
						uploaderVM.loading = false;
					}

					// 设置异常状态
					object.file.state = 'exception';
					// 中断正在上传的文件
					uploaderVM.uploader.stop(true);
					// 事件通知【upload-res-error】【File对象】【服务端错误信息】
					uploaderVM.$emit('upload-res-error', object.file, res);
				}
			});

			// 文件上传过程中创建进度条实时显示  
			this.uploader.on('uploadProgress', function(file, percentage) {
				// 校验图片的像素大小
				if (uploaderVM.pixelsWidth && uploaderVM.pixelsHeight) {

				}
				// 100计算百分比
				file.percentage = Math.round(percentage * 100);

				// 按钮模式
				if (!uploaderVM.type) {
					// 修改按钮文字
					uploaderVM.btnName = '上传中:' + file.percentage + '%';
				}

				// 事件通知【upload-progress】【File对象】[上传进度百分指]
				uploaderVM.$emit('upload-progress', uploaderVM.getFiles());

				// 记录上传文件列表
				if (!uploaderVM.type) {
					uploaderVM.fileList = uploaderVM.getFiles();
				}

			});

			// 上传成功后执行的方法  
			this.uploader.on('uploadSuccess', function(file, res) {
				// 取消加载中
				if (uploaderVM.type !== 'custom') {
					uploaderVM.loading = false;
				}

				// 删除文件的上传进度
				delete file.percentage;
				if (res.code === 200) {
					// 设置成功状态
					file.state = 'success';

					let databack = {};

					// 组装返回值
					if (res.data && res.data.fdfsInfo) {
						databack = res.data.fdfsInfo;
					}

					// 事件通知【upload-success】【File对象】【File数组】【文件信息（服务端）对象】
					uploaderVM.$emit('upload-success', file, uploaderVM.getFiles(), databack);

					// 图片模式，增加文件数据
					if (uploaderVM.type === 'picture') {
						uploaderVM.pictureFileList.push(file);
					}
				} else {
					// 按钮模式
					if (!uploaderVM.type) {
						// 修改按钮文字
						uploaderVM.btnName = uploaderVM.$options.data().btnName;
					}

					// 设置异常状态
					file.state = 'exception';
					// 中断正在上传的文件
					uploaderVM.uploader.stop(true);
					// 错误提示
					uploaderVM.$message({
						showClose: true,
						message: `${res.msg}`,
						type: 'error',
						duration: 3000
					});

					// 事件通知【upload-res-error】【File对象】【File数组】【服务端错误信息】
					uploaderVM.$emit('upload-res-error', file, uploaderVM.getFiles(), res);
				}

				// 记录上传文件列表
				if (!uploaderVM.type) {
					uploaderVM.fileList = uploaderVM.getFiles();
				}
			});

			// 所有文件上传成功后执行的方法  
			this.uploader.on('uploadFinished', function() {
				// 按钮模式
				if (!uploaderVM.type) {
					// 修改按钮文字
					uploaderVM.btnName = uploaderVM.$options.data().btnName;
				}

				// 取消加载中
				if (uploaderVM.type !== 'custom') {
					uploaderVM.loading = false;
				}

				// 事件通知【upload-finish】【Files】
				uploaderVM.$emit('upload-finish', uploaderVM.getFiles());

				// 记录上传文件列表
				if (!uploaderVM.type) {
					uploaderVM.fileList = uploaderVM.getFiles();
				}

				// 清空队列
				uploaderVM.reset();
			});

			// 上传出错后执行的方法  
			this.uploader.on('uploadError', function(file, reason) {
				// 设置异常状态
				file.state = 'exception';
				// 中断正在上传的文件
				uploaderVM.uploader.stop(true);
				// 错误提示
				uploaderVM.$message({
					showClose: true,
					message: `文件名：${file.name}上传失败，【错误码：${reason}】`,
					type: 'error',
					duration: 3000
				});
				// 事件通知【upload-error】【File对象】【File数组】
				uploaderVM.$emit('upload-error', file, uploaderVM.getFiles());
				// 记录上传文件列表
				if (!uploaderVM.type) {
					uploaderVM.fileList = uploaderVM.getFiles();
				}
			});

			//当validate不通过时，会以派送错误事件的形式通知调用者
			// 错误类型：
			//	- F_EXCEED_SIZE： 单个文件大小限制
			// 	- Q_EXCEED_NUM_LIMIT: 文件个数限制
			// 	- Q_EXCEED_SIZE_LIMIT: 所有文件大小总和限制
			// 	- Q_TYPE_DENIED:  文件类型限制
			this.uploader.on('error', function(type) {

				// 错误提示
				uploaderVM.$message({
					showClose: true,
					message: `${uploaderVM.errorMsg[type]}【错误码：${type}】`,
					type: 'error',
					duration: 3000
				});

				// 事件通知【upload-error】【错误类型】
				uploaderVM.$emit('error', type);
			});

			// 不管成功或者失败，文件上传完成时触发  
			this.uploader.on('uploadComplete', function(file) {
				// 事件通知【upload-complete】【File对象】【File数组】
				uploaderVM.$emit('upload-complete', file, uploaderVM.getFiles());
			});
		},

		/**
		 * description: 获取文件统计信息
		 * 
		 * @return {Object}
		 * 	- successNum 上传成功的文件数
		 *	- progressNum 上传中的文件数
		 *	- cancelNum 被删除的文件数
		 *	- invalidNum 无效的文件数
		 *	- uploadFailNum 上传失败的文件数
		 *	- queueNum 还在队列中的文件数
		 *	- interruptNum 被暂停的文件数
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		getStats() {
			return this.uploader.getStats();
		},

		/**
		 * description: 获取文件集合
		 * @param {String} stats 文件状态，可以是error等
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		getFiles(stats) {
			if (typeof stats === 'undefined') {
				return this.uploader.getFiles();
			} else {
				return this.uploader.getFiles(stats);
			}
		},

		/**
		 * description: 销毁 webuploader 实例
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		destroy() {
			// 如果正在上传，中断
			if (this.isInProgress()) {
				return;
			}

			this.uploader.destroy();
		},

		/**
		 * description: 删除要上传的文件
		 * @param {File} file 被删除的文件 也可以是文件的id
		 * @param {Boolean} b 默认true，移除队列中的文件，如果为false，队列中的文件取消上传，但不删除
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		removeFile(file, b) {
			// 如果正在上传，中断
			if (this.isInProgress()) {
				return;
			}

			// 默认从队列中移除
			if (b === undefined) {
				b = true;
			}

			this.uploader.removeFile(file, b);
		},

		/**
		 * description: 重试上传，重试指定文件，或者从出错的文件开始重新上传
		 * @param {File} file  可传可不传
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		retry(file) {
			// 如果正在上传，中断
			if (this.isInProgress()) {
				return;
			}

			if (file) {
				this.uploader.retry(file);
			} else {
				this.uploader.retry();
			}
		},

		/**
		 * description: 开始上传。此方法可以从初始状态调用开始上传流程，也可以从暂停状态调用，继续上传流程。可以指定开始某一个文件
		 * @param {File} file  可传可不传,也可以是文件id
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		upload(file) {
			// 如果正在上传，中断
			if (this.isInProgress()) {
				return;
			}

			if (typeof file === 'undefined') {
				this.uploader.upload();
			} else {
				this.uploader.upload(file);
			}
		},

		/**
		 * description: 重置uploader。目前只重置了队列
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		reset() {
			// 如果正在上传，中断
			if (this.isInProgress()) {
				return;
			}

			this.uploader.reset();
		},

		/**
		 * description: 暂停上传。第一个参数为是否中断上传当前正在上传的文件。如果第一个参数是文件，则只暂停指定文件。
		 * @param {Boolean | File | Fileid} file 
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		stop(file) {
			if (typeof file === 'undefined') {
				this.uploader.stop();
			} else {
				this.uploader.stop(file);
			}
		},

		/**
		 * description: 标记文件状态为已取消, 同时将中断文件传输。
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		cancelFile(file) {
			// 如果正在上传，中断
			if (this.isInProgress()) {
				return;
			}

			if (typeof file === 'undefined') {
				this.uploader.cancelFile();
			} else {
				this.uploader.cancelFile(file);
			}
		},

		/**
		 * description: 判断是否正在上传中。
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		isInProgress() {
			return this.uploader.isInProgress();
		},

		/**
		 * description: 掉过一个文件上传，直接标记指定文件为已上传状态。
		 * @param {File} file 跳过上传的文件
		 * 
		 * created by hezq on 2019-10-11
		 *
		 */
		skipFile(file) {
			// 如果正在上传，中断
			if (this.isInProgress()) {
				return;
			}

			this.uploader.skipFile(file);
		},
	},

	mounted() {
		// 注册上传控件
		this.registerUploader();
		// 创建上传实例
		this.createUploader();
		// 触发上传过程中的事件
		this.doUploaderEvents();
	},

	beforeDestroy() {
		// 销毁上传实例
		this.uploader.destroy();
		// 回收
		this.uploader = null;
	},
}



// WEBPACK FOOTER //
// ./src/components/upload/src/webuploader.js